import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const useDialogAccessibility = ({
  containerRef,
  initialFocusRef,
  isOpen,
  onClose,
  closeDisabled = false,
}) => {
  const onCloseRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  onCloseRef.current = onClose;
  closeDisabledRef.current = closeDisabled;

  useEffect(() => {
    if (!isOpen) return undefined;

    const dialog = containerRef.current;
    if (!dialog) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const inertedElements = [];
    let activeBranch = dialog;
    while (activeBranch.parentElement && activeBranch.parentElement !== document.body) {
      const siblings = Array.from(activeBranch.parentElement.children);
      for (const sibling of siblings) {
        if (sibling !== activeBranch && !sibling.hasAttribute("inert")) {
          sibling.setAttribute("inert", "");
          inertedElements.push(sibling);
        }
      }
      activeBranch = activeBranch.parentElement;
    }

    const focusableElements = () =>
      Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true"
      );

    const firstFocusable = focusableElements()[0];
    const focusFrame = window.requestAnimationFrame(() =>
      (initialFocusRef?.current ?? firstFocusable ?? dialog).focus()
    );

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !closeDisabledRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;

      const elements = focusableElements();
      if (elements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      inertedElements.forEach((element) => element.removeAttribute("inert"));
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [containerRef, initialFocusRef, isOpen]);
};

export default useDialogAccessibility;
